import { apiClient } from './client'
import type { ApiResponse } from '../types/api'
import type { AdminRole } from '../stores/authStore'

interface LoginPayload {
  email: string
  password: string
}

interface AdminSession {
  admin: { _id: string; email: string; role: AdminRole }
  tokens: { accessToken: string; refreshToken: string }
}

/**
 * A correct password no longer yields a session — only a short-lived (5 min)
 * `mfaToken` ticket for the second factor. Keep the ticket in component state;
 * it must never reach the persisted auth store or localStorage.
 */
export type LoginResponse =
  | { mfaRequired: true; mfaSetupRequired?: never; mfaToken: string }
  | { mfaSetupRequired: true; mfaRequired?: never; mfaToken: string }
  /** An API from before 2FA. Only seen while the portal is ahead of the API. */
  | (AdminSession & { mfaRequired?: never; mfaSetupRequired?: never })

interface RefreshResponse {
  accessToken: string
}

export interface MfaSetupStartResponse {
  otpauthUrl: string
  secret: string
}

export type MfaSetupConfirmResponse = AdminSession & { backupCodes: string[] }

export type MfaVerifyResponse = AdminSession

/** Exactly one of `code` (6-digit authenticator code) or `backupCode`. */
export type MfaVerifyPayload =
  | { mfaToken: string; code: string; backupCode?: never }
  | { mfaToken: string; backupCode: string; code?: never }

/** One authenticator app registered to the account. The API never sends secrets. */
export interface MfaDevice {
  _id: string
  name: string
  addedAt: string
  lastUsedAt: string | null
}

export interface MfaStatus {
  enabled: boolean
  enabledAt: string | null
  backupCodesRemaining: number
  /** Absent on an API from before multi-device 2FA (one authenticator per account). */
  devices?: MfaDevice[]
  maxDevices?: number
}

export interface MfaAddDevicePayload {
  name: string
  /** From the NEW device. */
  code: string
  /** From a device already on the account. */
  currentCode: string
}

export const authApi = {
  login: (payload: LoginPayload) =>
    apiClient.post<ApiResponse<LoginResponse>>('/admin/login', payload),

  mfaSetupStart: (mfaToken: string) =>
    apiClient.post<ApiResponse<MfaSetupStartResponse>>('/admin/mfa/setup/start', { mfaToken }),

  /** `deviceName` is ignored by an API from before multi-device 2FA. */
  mfaSetupConfirm: (mfaToken: string, code: string, deviceName?: string) =>
    apiClient.post<ApiResponse<MfaSetupConfirmResponse>>('/admin/mfa/setup/confirm', {
      mfaToken,
      code,
      ...(deviceName ? { deviceName } : {}),
    }),

  mfaVerify: (payload: MfaVerifyPayload) =>
    apiClient.post<ApiResponse<MfaVerifyResponse>>('/admin/mfa/verify', payload),

  /** "Can't use your authenticator?": emails the account a 2FA reset link. Same answer whether or not one was sent. */
  mfaRecoveryRequest: (mfaToken: string) =>
    apiClient.post<ApiResponse<{ sentTo: string }>>('/admin/mfa/recovery/request', { mfaToken }),

  /** Uses the emailed link: clears the account's 2FA so the next sign-in sets it up again. */
  mfaRecoveryConfirm: (token: string) =>
    apiClient.post<ApiResponse<undefined>>('/admin/mfa/recovery/confirm', { token }),

  mfaStatus: () => apiClient.get<ApiResponse<MfaStatus>>('/admin/mfa/status'),

  /** Requires a current authenticator code; invalidates every earlier backup code. */
  regenerateBackupCodes: (code: string) =>
    apiClient.post<ApiResponse<{ backupCodes: string[] }>>('/admin/mfa/backup-codes/regenerate', {
      code,
    }),

  /** New pending secret for another authenticator device. */
  mfaDeviceStart: () =>
    apiClient.post<ApiResponse<MfaSetupStartResponse>>('/admin/mfa/devices/start'),

  mfaDeviceConfirm: (payload: MfaAddDevicePayload) =>
    apiClient.post<ApiResponse<{ devices: MfaDevice[] }>>('/admin/mfa/devices/confirm', payload),

  /** `currentCode` may come from any device, including the one being removed. */
  mfaDeviceRemove: (deviceId: string, currentCode: string) =>
    apiClient.delete<ApiResponse<{ devices: MfaDevice[] }>>(`/admin/mfa/devices/${deviceId}`, {
      data: { currentCode },
    }),

  refreshToken: (refreshToken: string) =>
    apiClient.post<ApiResponse<RefreshResponse>>('/admin/refresh-token', { refreshToken }),

  /** Revokes this browser's refresh token server-side. Other sessions survive. */
  logout: (refreshToken: string) =>
    apiClient.post<ApiResponse<null>>('/admin/logout', { refreshToken }),
}
